import React, { useEffect, useRef, useState } from "react";
import { fileNameFromUrl, formatStamp, formatDayTime, periodSpanText } from "../api/shape.js";
import { periodKey, periodServerMessage, validatePeriodInput } from "../api/reportPeriod.js";
import { normalisePublishedNumber, reportRef } from "../api/reportLabel.js";
import { buildSteps, activeGeneration, confirmationValid } from "../api/reportSteps.js";
import { usePhone } from "../phone/usePhone.js";
import PhoneReport from "../phone/PhoneReport.jsx";
import Dialog from "./report/Dialog.jsx";
import { ApproveBody, PublishBody } from "./report/DialogBodies.jsx";
import { ReadinessLines } from "./report/Readiness.jsx";
import ReportsTable, { VersionLink } from "./report/ReportsTable.jsx";
import { generationNotes, unapprovedRecordsText } from "./report/text.js";
import { ApprovingPanel, ErrorBox, GeneratingPanel, PdfReadyCard, PdfRetryPanel, PublishedCard, PublishingPanel } from "./report/Panels.jsx";
import { STATUS_TEXT, outcomeText } from "./report/text.js";
import { afterApproveStep, formatSiteTime } from "../api/reportSchedule.js";

export { VersionLink };

const TERMINAL_JOB_STATES = ["ready", "no_prose", "refused_no_data", "failed", "abandoned"];

/** Which step a flow panel (approving, PDF, publishing...) belongs under. */
function flowStepIndex(flow) {
  if (flow.kind === "approving" || flow.kind === "retrying") return 4;
  if (flow.kind === "error") return flow.op === "publish" || flow.op === "schedule" || flow.op === "hold" ? 5 : 4;
  return 5;
}

const ASIDE_BODY =
  "1. Every line under Check the data is green. 2. You confirm the data. 3. Generate builds a Word draft. 4. Edit any draft in Word if needed. 5. Approve the draft you want; a PDF is made. 6. Publish puts the PDF on the project site for the client.";

/**
 * Weekly report screen v2 -- the owner-approved design (DEC-CHAT-20261010-403:
 * mockups/cockpit-v3/weekly-report/Main.dc.html and Approve.dc.html). Per
 * project: a six-step list (check the data, confirm, generate, review and edit
 * in Word, approve, publish) beside "How a report is made", and a real table of
 * every report where any draft can be edited in Word and approved.
 *
 * `model` is shape.js#shapeReportPage's output (live) or the fixture
 * equivalent -- both render through this one component. Every act has its own
 * confirmation (a modal dialog for approve and publish; inline for the
 * readiness actions) and every server outcome is shown in words -- a
 * 422/409/read-back failure is never swallowed and never auto-retried. The
 * screen never advances state optimistically: after each write it shows the
 * server's own row (App.jsx re-reads the page).
 */
export default function Report({
  model,
  me = "",
  today = new Date().toISOString().slice(0, 10),
  pollMs = 4000,
  onGenerate,
  onJobStatus,
  onApprove,
  onReopen,
  onRemindPhoto,
  onWaivePhoto,
  onLoadVersions,
  onPublish,
  onRenderPdf,
  // SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001 section 6: schedule / hold an approved report.
  onSchedule,
  onHold,
  onPrevWeek,
  onNextWeek,
  onBackToCurrent,
  // SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.5: the Custom period control.
  // `canCustomPeriod` is the sm: gate (only site managers see it);
  // `onSelectPeriod({ periodStart, periodEnd })` reads readiness for a valid
  // pair; `onClearPeriod()` is "Week"; `periodError` is the server's refusal
  // of the last period (an `errorCode` body).
  canCustomPeriod = false,
  periodError = null,
  onSelectPeriod,
  onClearPeriod,
  onNavigate,
  resolveMissingResult,
  onRemindResult,
  // Re-reads the report page quietly while a queued generation is running.
  onRefresh,
  weekNavLoading = false,
  // Render-test / screenshot seams only (react-dom/server never fires a click).
  initialCreated = null,
  initialOpenLines = {},
  initialPendingAction = null,
  initialPeriodOpen = false,
  initialPeriodFrom = "",
  initialPeriodTo = "",
  initialDialog = null,
  initialFlow = {},
  initialConfirmed = {},
  initialGen = {},
  initialOpenHistory = {},
  // Phone render-test seam only: open Check the data for this project code.
  initialPhoneCheck = null,
}) {
  const phone = usePhone();
  const [pendingAction, setPendingAction] = useState(initialPendingAction); // inline readiness acts: { code, kind, reason? }
  const [dialog, setDialog] = useState(initialDialog); // modal: { kind: approve|publish|reopen|regenerate, code, ... }
  const [flow, setFlow] = useState(initialFlow); // code -> approve / PDF / publish progress or refusal
  const [confirmed, setConfirmed] = useState(initialConfirmed); // code -> { hash, atUtc, by, view } the "data complete" tick
  const [gen, setGen] = useState(initialGen); // code -> generation this browser started
  const [outcome, setOutcome] = useState({}); // code -> { errorCode, ... } | { status }
  const [openLines, setOpenLines] = useState(initialOpenLines); // code -> readiness line id
  const [openHistory, setOpenHistory] = useState(initialOpenHistory); // `${code}-${no}` -> true
  const [proseOff, setProseOff] = useState({}); // code -> true when the site manager clears the written summary
  const [created, setCreated] = useState(initialCreated); // { code, number } after a generation finishes
  const [tick, setTick] = useState(0);
  const [periodOpen, setPeriodOpen] = useState(initialPeriodOpen || Boolean(model.period));
  const [periodFrom, setPeriodFrom] = useState(initialPeriodFrom || (model.period ? model.period.periodStart : ""));
  const [periodTo, setPeriodTo] = useState(initialPeriodTo || (model.period ? model.period.periodEnd : ""));
  const inflight = useRef(new Set()); // `${code}:${act}` -- an act already running cannot start again
  const modelRef = useRef(model);
  modelRef.current = model;
  const viewKey = model.period ? periodKey(model.period) : model.weekStart || "";

  // --- Generation: follow the job until it ends ---------------------------
  // A generation is followed from either side: the one this browser started
  // (`gen`) or the backend's own signal on the page (`project.generating`,
  // from the per-project generation claim), so a reload still shows progress.
  const pollTargets = model.projects
    .map((p) => {
      const local = gen[p.code] || null;
      if (!local && !p.generating) return null;
      return { code: p.code, jobId: (local && local.jobId) || (p.generating && p.generating.jobId) || "", local: Boolean(local) };
    })
    .filter(Boolean);
  const pollKey = pollTargets.map((t) => `${t.code}:${t.jobId}`).join("|");

  useEffect(() => {
    if (!pollTargets.length || !onRefresh) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await onRefresh();
      if (cancelled) return;
      for (const t of pollTargets) {
        if (!t.jobId) continue; // the start call has not answered yet
        let status = null;
        if (onJobStatus) {
          try {
            status = await onJobStatus(t.code, t.jobId);
          } catch {
            status = null;
          }
        }
        if (cancelled) return;
        const terminal = Boolean(status && TERMINAL_JOB_STATES.includes(status.state));
        // An omitted or unknown `generationInProgress` is NOT "done": the server omits it
        // when its own claim read fails. Only the job's explicit terminal status ends it.
        if (terminal) finishGeneration(t, status);
      }
      if (!cancelled) setTick((n) => n + 1);
    }, pollMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollKey, tick]);

  function finishGeneration(target, status) {
    setGen((prev) => {
      if (!prev[target.code]) return prev;
      const next = { ...prev };
      delete next[target.code];
      return next;
    });
    if (status && (status.state === "failed" || status.state === "refused_no_data" || status.state === "abandoned")) {
      setOutcome((prev) => ({ ...prev, [target.code]: { errorCode: status.state === "abandoned" ? "failed" : status.state === "failed" ? status.errorCode || "failed" : status.state } }));
    } else {
      setCreated({ code: target.code, number: (status && status.reportNumber) || null, notes: generationNotes(status), unapproved: Number(status && status.unapprovedRecords) || 0 });
    }
    if (onRefresh) onRefresh();
  }

  useEffect(() => {
    if (!created) return undefined;
    // A draft with something to check stays on screen longer than a plain one.
    const timer = setTimeout(() => setCreated(null), (created.notes && created.notes.length) || created.unapproved ? 60000 : 10000);
    return () => clearTimeout(timer);
  }, [created]);

  // A finished approve / PDF / publish message belongs to the view it happened
  // in: changing week or period drops it (a call still running keeps its panel).
  useEffect(() => {
    setFlow((prev) => {
      const next = {};
      for (const [code, f] of Object.entries(prev)) {
        if (f.kind === "approving" || f.kind === "retrying" || f.kind === "publishing" || f.kind === "scheduling") next[code] = f;
      }
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [viewKey]);

  function setFlowFor(code, value) {
    setFlow((prev) => {
      const next = { ...prev };
      if (value) next[code] = value;
      else delete next[code];
      return next;
    });
  }

  function confirmedFor(project) {
    const c = confirmed[project.code];
    return c && c.view === viewKey && confirmationValid(project, c) ? c : null;
  }

  // --- Acts ----------------------------------------------------------------
  function confirmData(project) {
    if (!project.allGreen) return;
    setConfirmed((prev) => ({ ...prev, [project.code]: { hash: project.readinessHash, atUtc: new Date().toISOString(), by: me, view: viewKey } }));
  }

  async function generate(project, { reason } = {}) {
    const code = project.code;
    const key = `${code}:generate`;
    if (inflight.current.has(key) || gen[code] || project.generating || project.generationElsewhere || !onGenerate) return;
    inflight.current.add(key);
    // Show it at once: the panel, the disabled button, who and when.
    setGen((prev) => ({
      ...prev,
      [code]: { jobId: "", startedAt: new Date().toISOString(), by: me, reportNumber: project.current ? project.current.reportNumber : project.nextReportNumber, view: viewKey },
    }));
    setOutcome((prev) => ({ ...prev, [code]: null }));
    setCreated(null);
    setFlowFor(code, null);
    let result;
    try {
      result = await onGenerate(code, { prose: !proseOff[code], reason });
    } catch {
      result = { errorCode: "network_error" };
    }
    inflight.current.delete(key);
    if (result && result.errorCode) {
      setGen((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setOutcome((prev) => ({ ...prev, [code]: result }));
      return;
    }
    if (result && result.jobId) {
      setGen((prev) => (prev[code] ? { ...prev, [code]: { ...prev[code], jobId: result.jobId } } : prev));
    } else {
      // The server answered with the finished report (the direct path).
      setGen((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setCreated({ code, number: (result && result.reportNumber) || null, notes: generationNotes(result ? { ...result, state: result.refusedNoData ? "refused_no_data" : result.llmRunId ? "ready" : "no_prose" } : null), unapproved: Number(result && result.unapprovedRecords) || 0 });
    }
  }

  function askApprove(project, row) {
    // Section 6: with a schedule on offer the dialog asks what follows the
    // approval, Schedule (default) or Publish now.
    const scheduleDefault = onSchedule && project.scheduleDefault ? project.scheduleDefault : null;
    setDialog({
      kind: "approve", code: project.code, reportNumber: row.reportNumber, etag: row.etag, version: row.displayVersion, periodText: row.periodText, generatedAt: row.generatedAt, editedAt: row.editedAt, confirmedAt: (row.raw && row.raw.dataConfirmedAtUtc) || "",
      ...(scheduleDefault ? { scheduleDefault, next: "schedule" } : {}),
    });
  }

  function scheduledFlow(reportNumber, version, row) {
    return {
      kind: "scheduled", op: "schedule", reportNumber, version,
      when: row && row.publishAtUtc ? `${formatSiteTime(row.publishAtUtc, row.publishTimeZone)} (${row.publishTimeZone || "site time"})` : "",
    };
  }

  async function runSchedule(code, reportNumber, etag, version) {
    const key = `${code}:${reportNumber}:schedule`;
    if (!onSchedule || inflight.current.has(key)) return;
    inflight.current.add(key);
    setFlowFor(code, { kind: "scheduling", op: "schedule", reportNumber, version });
    let result;
    try {
      result = await onSchedule(code, reportNumber, etag);
    } catch {
      result = { errorCode: "network_error" };
    }
    inflight.current.delete(key);
    if (result && result.errorCode) {
      setFlowFor(code, { kind: "error", op: "schedule", reportNumber, errorCode: result.errorCode, message: outcomeText(result.errorCode, result, "schedule") });
      return;
    }
    setFlowFor(code, scheduledFlow(reportNumber, version, result));
  }

  async function doHold(project, row) {
    const code = project.code;
    const key = `${code}:${row.reportNumber}:hold`;
    if (!onHold || inflight.current.has(key)) return;
    inflight.current.add(key);
    let result;
    try {
      result = await onHold(code, row.reportNumber, row.etag);
    } catch {
      result = { errorCode: "network_error" };
    }
    inflight.current.delete(key);
    if (result && result.errorCode) {
      setFlowFor(code, { kind: "error", op: "hold", reportNumber: row.reportNumber, errorCode: result.errorCode, message: outcomeText(result.errorCode, result, "hold") });
      return;
    }
    setFlowFor(code, { kind: "held", op: "hold", reportNumber: row.reportNumber });
  }

  async function doApprove(d) {
    const key = `${d.code}:${d.reportNumber}:approve`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);
    setDialog(null);
    setOutcome((prev) => ({ ...prev, [d.code]: null }));
    setFlowFor(d.code, { kind: "approving", op: "approve", reportNumber: d.reportNumber, version: d.version, at: new Date().toISOString() });
    let result;
    try {
      result = await onApprove(d.code, d.reportNumber, d.etag);
    } catch {
      result = { errorCode: "network_error" };
    }
    inflight.current.delete(key);
    if (result && result.errorCode) {
      setFlowFor(d.code, { kind: "error", op: "approve", reportNumber: d.reportNumber, errorCode: result.errorCode, message: outcomeText(result.errorCode, result, "approve") });
      return;
    }
    if (result && result.pdf && result.pdf.status === "failed") {
      setFlowFor(d.code, { kind: "error", op: "pdf", reportNumber: d.reportNumber, errorCode: "pdf_conversion_failed", message: "The report is approved, but the PDF could not be created. Use Retry PDF." });
      return;
    }
    // Section 6: Confirm runs approve, then schedule or publish. A failed
    // second step leaves the row Approved; its error shows, and the row keeps
    // Schedule and Publish now.
    const step = afterApproveStep(d, result, { canSchedule: Boolean(onSchedule), canPublish: Boolean(onPublish) });
    if (step && step.action === "schedule") {
      await runSchedule(d.code, d.reportNumber, step.etag, d.version);
      return;
    }
    if (step && step.action === "publish") {
      await doPublish({ ...d, etag: step.etag, fileName: fileNameFromUrl((result && (result.pdfUrl || (result.pdf && result.pdf.url))) || "") });
      return;
    }
    setFlowFor(d.code, { kind: "pdf_ready", op: "approve", reportNumber: d.reportNumber, version: d.version, fileName: fileNameFromUrl((result && (result.pdfUrl || (result.pdf && result.pdf.url))) || "") });
  }

  async function retryPdf(project, row) {
    const key = `${project.code}:${row.reportNumber}:pdf`;
    if (!onRenderPdf || inflight.current.has(key)) return;
    inflight.current.add(key);
    setFlowFor(project.code, { kind: "retrying", op: "pdf", reportNumber: row.reportNumber });
    let result;
    try {
      result = await onRenderPdf(project.code, row.reportNumber, row.etag);
    } catch {
      result = { errorCode: "network_error" };
    }
    inflight.current.delete(key);
    if (result && result.errorCode) {
      setFlowFor(project.code, { kind: "error", op: "pdf", reportNumber: row.reportNumber, errorCode: result.errorCode, message: outcomeText(result.errorCode, result) });
      return;
    }
    if (result && result.pdf && result.pdf.status === "failed") {
      setFlowFor(project.code, { kind: "error", op: "pdf", reportNumber: row.reportNumber, errorCode: "pdf_conversion_failed", message: outcomeText("pdf_conversion_failed") });
      return;
    }
    setFlowFor(project.code, { kind: "pdf_ready", op: "pdf", reportNumber: row.reportNumber, version: row.displayVersion, fileName: fileNameFromUrl((result && (result.pdfUrl || (result.pdf && result.pdf.url))) || "") });
  }

  function askPublish(project, row) {
    setDialog({ kind: "publish", code: project.code, reportNumber: row.reportNumber, etag: row.etag, version: row.displayVersion, fileName: fileNameFromUrl(row.pdfOpenUrl) });
  }

  async function doPublish(d) {
    const key = `${d.code}:${d.reportNumber}:publish`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);
    setDialog(null);
    setOutcome((prev) => ({ ...prev, [d.code]: null }));
    setFlowFor(d.code, { kind: "publishing", op: "publish", reportNumber: d.reportNumber, version: d.version });
    let result;
    try {
      result = await onPublish(d.code, d.reportNumber, d.etag);
    } catch {
      result = { errorCode: "network_error" };
    }
    inflight.current.delete(key);
    if (result && result.errorCode) {
      setFlowFor(d.code, { kind: "error", op: "publish", reportNumber: d.reportNumber, errorCode: result.errorCode, message: outcomeText(result.errorCode, result, "publish"), url: result.url || "" });
      return;
    }
    setFlowFor(d.code, {
      kind: "published", op: "publish", reportNumber: d.reportNumber, publishedNumber: normalisePublishedNumber(result && result.publishedNumber), version: d.version,
      stamp: result && result.issuedAtUtc ? formatDayTime(result.issuedAtUtc) : "",
      siteUrl: (result && result.publishedUrl) || "",
    });
  }

  async function doReopen(d, reason) {
    setDialog(null);
    const result = await onReopen(d.code, d.reportNumber, d.etag, reason.trim());
    setFlowFor(d.code, null);
    setOutcome((prev) => ({ ...prev, [d.code]: result }));
  }

  // --- Inline readiness acts (remind / accept without a photo) ---------------
  function startInline(code, kind, extra) {
    setPendingAction({ code, kind, reason: "", ...extra });
    setOutcome((prev) => ({ ...prev, [code]: null }));
  }

  async function confirmInline() {
    if (!pendingAction) return;
    const { code, kind, inspectionNumber, etag, reason } = pendingAction;
    const key = `${code}:${kind}:${inspectionNumber}`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);
    // The period on screen (null on a week) rides along so a write never
    // replaces period readiness with week readiness (section 4.2).
    const result = kind === "remindPhoto"
      ? await onRemindPhoto(code, inspectionNumber, model.period)
      : await onWaivePhoto(code, inspectionNumber, etag, reason.trim(), model.period);
    inflight.current.delete(key);
    setPendingAction(null);
    setOutcome((prev) => ({ ...prev, [code]: result }));
  }

  // --- Custom period (SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.5) ---------
  const showPeriodControl = Boolean(canCustomPeriod && onSelectPeriod && model.periodSupported);
  // Client-side first, in the section 4.1 order; then the server's own code.
  const periodClientMessage = periodOpen ? validatePeriodInput({ from: periodFrom, to: periodTo, today }) : null;
  const periodServerText = periodError ? periodServerMessage(periodError.errorCode, periodError.conflict) : null;
  const periodMessage = periodClientMessage || (periodOpen ? periodServerText : null);
  const customOnScreen = Boolean(model.period);
  // Open with no valid pair yet: the week below is not the period being chosen.
  const periodPending = periodOpen && !customOnScreen;

  function changePeriod(nextFrom, nextTo) {
    setPeriodFrom(nextFrom);
    setPeriodTo(nextTo);
    if (!nextFrom || !nextTo || validatePeriodInput({ from: nextFrom, to: nextTo, today })) return;
    if (model.period && model.period.periodStart === nextFrom && model.period.periodEnd === nextTo) return;
    onSelectPeriod({ periodStart: nextFrom, periodEnd: nextTo });
  }

  function useWeek() {
    setPeriodOpen(false);
    setPeriodFrom("");
    setPeriodTo("");
    if (onClearPeriod) onClearPeriod();
  }

  const header = model.header;
  const dialogOpen = Boolean(dialog);

  // Phone layout (Report.dc.html and its screens): the same state and handlers,
  // drawn as a next-step card, report cards and sheets. This mirrors the
  // per-project derivation the desktop render below does inline.
  function phoneProjectView(project) {
    const code = project.code;
    const localAny = gen[code] || null;
    const local = localAny && (!localAny.view || localAny.view === viewKey) ? localAny : null;
    const projectFlow = flow[code] || null;
    const current = project.current;
    const currentFlow = projectFlow && current && projectFlow.reportNumber === current.reportNumber ? projectFlow : null;
    const built = buildSteps(project, {
      confirmed: confirmedFor(project),
      generating: local,
      approving: currentFlow && currentFlow.kind === "approving" ? currentFlow : null,
      publishing: Boolean(currentFlow && currentFlow.kind === "publishing"),
      me,
      today,
    });
    return {
      built,
      gening: activeGeneration(project, local),
      local,
      localAny,
      flow: projectFlow,
      currentFlow,
      flowRow: projectFlow ? project.archive.find((r) => r.reportNumber === projectFlow.reportNumber) || null : null,
      hasSteps: project.mode !== "covered" && project.mode !== "legacy",
      row: project.currentRow,
      step: built.steps.find((s) => s.state === "current") || null,
      flowBusy: Boolean(projectFlow && (projectFlow.kind === "approving" || projectFlow.kind === "retrying" || projectFlow.kind === "publishing")),
      outcome: outcome[code] || null,
    };
  }
  if (phone) {
    const hasNext = Boolean(model.period) || (header && !header.atCurrent);
    return (
      <PhoneReport
        ctx={{
          model, header, today, me, showPeriodControl, periodOpen, setPeriodOpen, periodFrom, periodTo, changePeriod, useWeek,
          periodMessage, periodServerText, onPrevWeek, onNextWeek, onBackToCurrent, weekNavLoading,
          initialPhoneCheck, projectView: phoneProjectView, created, setCreated, setFlowFor, confirmData, generate, askApprove, askPublish, retryPdf,
          // The phone dialog has no Schedule choice (yet): its Confirm approves only, as before.
          dialog, setDialog, doApprove: (d) => doApprove({ ...d, scheduleDefault: null }), doPublish, doReopen, pendingAction, setPendingAction, startInline, confirmInline,
          resolveMissingResult, onRemindResult, onRemindPhoto, onWaivePhoto, onNavigate,
          canPublish: Boolean(onPublish), canRetry: Boolean(onRenderPdf), openHistory,
          onToggleHistory: (key) => setOpenHistory((prev) => ({ ...prev, [key]: !prev[key] })), onLoadVersions,
          publishedNext: hasNext ? { label: model.period ? "Back to weeks" : "Go to next week", run: () => { if (model.period) useWeek(); else if (onNextWeek) onNextWeek(); } } : null,
        }}
      />
    );
  }

  return (
    <div className="page wr-page" data-testid="report-screen">
      <main className="wr-main" aria-hidden={dialogOpen ? "true" : undefined}>
        <div className="wr-head">
          <h1 className="wr-h1">Weekly reports</h1>
          {showPeriodControl ? (
            <div role="group" aria-label="Report period" className="wr-seg" data-testid="report-period-control">
              <button type="button" data-testid="report-use-week" aria-pressed={!periodOpen} onClick={useWeek}>Week</button>
              <button type="button" data-testid="report-custom-period" aria-pressed={periodOpen} onClick={() => setPeriodOpen(true)}>Custom period</button>
            </div>
          ) : null}
          {header && !periodOpen && !header.custom ? (
            <div className="wr-weeknav">
              {onPrevWeek && onNextWeek ? (
                <button type="button" data-testid="report-prev-week" aria-label={`Previous week, ${header.prevLabel.replace("‹ ", "")}`} disabled={weekNavLoading} onClick={onPrevWeek}>
                  {header.prevLabel}
                </button>
              ) : null}
              <span className="wr-weektitle" data-testid="report-header-title" aria-live="polite">{header.text}</span>
              {onPrevWeek && onNextWeek ? (
                // Disabled at the current week OR while a load is in flight: the
                // report pipeline never runs ahead of the calendar week, and a
                // click must never outrun the fetch (App.jsx enforces the real
                // boundary; this is the UI-level backstop).
                <button type="button" data-testid="report-next-week" aria-label={`Next week, ${header.nextLabel.replace(" ›", "")}`} disabled={header.atCurrent || weekNavLoading} onClick={onNextWeek}>
                  {header.nextLabel}
                </button>
              ) : null}
              {!header.atCurrent && onBackToCurrent ? (
                <button type="button" className="wr-ghost" data-testid="report-back-to-current" disabled={weekNavLoading} onClick={onBackToCurrent}>
                  Back to this week
                </button>
              ) : null}
            </div>
          ) : null}
          {showPeriodControl && periodOpen ? (
            <div className="wr-custom" data-testid="report-period-form">
              <label htmlFor="wr-from">From</label>
              <input id="wr-from" className="wr-input" type="date" data-testid="report-period-from" value={periodFrom} max={today} onChange={(e) => changePeriod(e.target.value, periodTo)} />
              <label htmlFor="wr-to">To</label>
              <input id="wr-to" className="wr-input" type="date" data-testid="report-period-to" value={periodTo} max={today} onChange={(e) => changePeriod(periodFrom, e.target.value)} />
              {customOnScreen && !periodClientMessage ? <span className="wr-spanhint" data-testid="report-header-range">{periodSpanText(model.period.periodStart, model.period.periodEnd)}</span> : null}
            </div>
          ) : null}
        </div>
        {periodMessage ? (
          <div role="alert" data-testid="report-period-error" className="wr-warnmsg">{periodMessage}</div>
        ) : null}
        {periodPending && !periodMessage ? (
          <div data-testid="report-period-hint" className="wr-hint">Choose a From and a To date to see readiness for that period.</div>
        ) : null}

        {model.errors.length > 0 ? (
          <div data-testid="report-errors" className="wr-errbox">
            {model.errors.map((e) => (
              <span key={e.code}><strong>{e.code}</strong> Unavailable &mdash; {e.error}</span>
            ))}
          </div>
        ) : null}

        {(periodPending ? [] : model.projects).map((project) => {
          const code = project.code;
          // A generation belongs to the week/period it was started on.
          const localAny = gen[code] || null;
          const local = localAny && (!localAny.view || localAny.view === viewKey) ? localAny : null;
          const projectFlow = flow[code] || null;
          const current = project.current;
          const currentFlow = projectFlow && current && projectFlow.reportNumber === current.reportNumber ? projectFlow : null;
          const otherFlow = projectFlow && !currentFlow ? projectFlow : null;
          const built = buildSteps(project, {
            confirmed: confirmedFor(project),
            generating: local,
            approving: currentFlow && currentFlow.kind === "approving" ? currentFlow : null,
            publishing: Boolean(currentFlow && currentFlow.kind === "publishing"),
            me,
            today,
          });
          const gening = activeGeneration(project, local);
          const hasSteps = project.mode !== "covered" && project.mode !== "legacy";
          const summary = hasSteps ? built.summary : "";
          const selectedLine = openLines[code] || null;
          const projectOutcome = outcome[code];
          const flowBusy = Boolean(projectFlow && (projectFlow.kind === "approving" || projectFlow.kind === "retrying" || projectFlow.kind === "publishing" || projectFlow.kind === "scheduling"));
          const row = project.currentRow;

          function stepAction(step) {
            if (step.key === "confirm") return () => confirmData(project);
            if (step.key === "generate") return () => generate(project);
            if (step.key === "review") return () => askApprove(project, row);
            if (step.key === "approve") return () => retryPdf(project, row);
            if (step.key === "publish") return () => askPublish(project, row);
            return undefined;
          }

          function renderFlow(f) {
            if (f.kind === "approving") {
              return <ApprovingPanel code={code} title={`Approved${f.version ? ` v${f.version}` : ""} · ${formatStamp(f.at, today)}`} detail="Usually under 30 seconds. The buttons stay off until it is done." />;
            }
            if (f.kind === "retrying") return <PdfRetryPanel code={code} />;
            if (f.kind === "publishing") return <PublishingPanel code={code} title="Publishing the approved report…" />;
            if (f.kind === "pdf_ready") return <PdfReadyCard code={code} version={f.version} fileName={f.fileName} />;
            if (f.kind === "scheduling") return <PublishingPanel code={code} title="Approved · scheduling the publication…" />;
            if (f.kind === "scheduled") {
              return (
                <div className="wr-created" data-testid={`report-scheduled-${code}`} role="status">
                  Approved and scheduled{f.when ? ` · publishes ${f.when}` : ""}. Until then you can hold it, publish it now, or reopen it.
                </div>
              );
            }
            if (f.kind === "held") {
              return <div className="wr-created" data-testid={`report-held-${code}`} role="status">Held. The report stays approved and is not published until you schedule or publish it.</div>;
            }
            if (f.kind === "published") {
              const hasNext = Boolean(model.period) || (header && !header.atCurrent);
              return (
                <PublishedCard
                  code={code}
                  label={f.publishedNumber ? `Report ${f.publishedNumber}` : "The report"}
                  stamp={f.stamp}
                  siteUrl={f.siteUrl || (project.currentRow && project.currentRow.publishedUrl) || ""}
                  nextLabel={model.period ? "Back to weeks" : "Go to next week"}
                  onNext={hasNext ? () => { setFlowFor(code, null); if (model.period) useWeek(); else if (onNextWeek) onNextWeek(); } : undefined}
                />
              );
            }
            if (f.kind === "error") {
              const title = f.op === "publish" ? "The approved report was not published"
                : f.op === "schedule" ? "The approved report was not scheduled"
                : f.op === "hold" ? "The schedule was not held"
                : f.op === "pdf" ? "PDF not created" : "The draft was not approved";
              return <ErrorBox code={code} title={title} message={f.message} url={f.errorCode === "already_published" ? f.url : ""} onDismiss={() => setFlowFor(code, null)} />;
            }
            return null;
          }

          return (
            <section className="card wr-project" key={code} data-testid={`report-project-${code}`} aria-labelledby={`wr-h-${code}`}>
              <div className="wr-phead">
                <h2 id={`wr-h-${code}`}>{code}</h2>
                <span className="wr-pperiod">Report for {project.periodText}</span>
                <span className="wr-psummary" data-testid={`report-summary-${code}`}>{summary}</span>
              </div>

              <div className="wr-pbody">
                {hasSteps ? (
                  <ol aria-label="Steps for this period" className="wr-steps" data-testid={`report-steps-${code}`}>
                    {built.steps.map((step, i) => {
                      const last = i === built.steps.length - 1;
                      const isCurrent = step.state === "current";
                      const action = stepAction(step);
                      const generateBlocked = step.key === "generate" && isCurrent && !local && Boolean(project.generationElsewhere || localAny);
                      const flowHere = projectFlow && currentFlow && flowStepIndex(currentFlow) === i ? currentFlow : null;
                      const retrying = Boolean(currentFlow && currentFlow.kind === "retrying" && step.key === "approve");
                      const primaryLocked = step.primaryLocked || generateBlocked || retrying || flowBusy;
                      const hasActions = Boolean(step.primary || step.secondary || step.lockNote);
                      return (
                        <li
                          className="wr-step"
                          key={step.key}
                          data-state={step.state}
                          data-locked-action={step.primaryLocked && step.lockNote ? "true" : undefined}
                          data-testid={`report-step-${code}-${step.key}`}
                          aria-current={isCurrent ? "step" : undefined}
                        >
                          <span className="wr-railcol">
                            <span className="wr-dot" aria-hidden="true">{step.mark}</span>
                            {!last ? <span className="wr-rail" aria-hidden="true" /> : null}
                          </span>
                          <div className="wr-sbody">
                            <div className="wr-stitle">
                              <strong>{step.title}</strong>
                              <span className="wr-meta">{step.meta}</span>
                            </div>
                            {isCurrent ? <span className="wr-help">{step.help}</span> : null}

                            {step.showLines ? (
                              <>
                                <ReadinessLines
                                  project={project}
                                  openLine={selectedLine}
                                  onToggle={(lineId) => setOpenLines((prev) => ({ ...prev, [code]: prev[code] === lineId ? null : lineId }))}
                                  detailProps={{
                                    onNavigate,
                                    resolveMissingResult,
                                    onRemindResult,
                                    onRemindPhoto: onRemindPhoto ? (item) => startInline(code, "remindPhoto", { inspectionNumber: item.inspectionNumber, label: `CI-${item.inspectionNumber} ${item.title || ""}`.trim() }) : undefined,
                                    onWaivePhoto: onWaivePhoto ? (item) => startInline(code, "waivePhoto", { inspectionNumber: item.inspectionNumber, etag: item.etag, label: `CI-${item.inspectionNumber} ${item.title || ""}`.trim() }) : undefined,
                                  }}
                                />
                                {pendingAction && pendingAction.code === code ? (
                                  <div data-testid={`report-confirm-${code}`} className="wr-status" style={{ maxWidth: 460 }}>
                                    <strong style={{ fontWeight: 600 }}>
                                      Confirm: {pendingAction.kind === "remindPhoto" ? `Remind the inspector to add a photo to ${pendingAction.label}` : `Accept ${pendingAction.label} without a photo`}
                                    </strong>
                                    {pendingAction.kind === "waivePhoto" ? (
                                      <textarea
                                        value={pendingAction.reason}
                                        onChange={(e) => setPendingAction({ ...pendingAction, reason: e.target.value })}
                                        placeholder="Reason (required)"
                                        aria-label="Reason for accepting without a photo"
                                        maxLength={500}
                                        data-testid={`report-reason-${code}`}
                                        style={{ minHeight: 50, background: "var(--surface)", color: "var(--t1)", border: "1px solid var(--line2)", borderRadius: 6, padding: 8, font: "inherit" }}
                                      />
                                    ) : null}
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <button
                                        type="button"
                                        className="wr-btn pri"
                                        style={{ height: 34 }}
                                        disabled={pendingAction.kind === "waivePhoto" && !pendingAction.reason.trim()}
                                        onClick={confirmInline}
                                      >
                                        Confirm
                                      </button>
                                      <button type="button" className="wr-btn" style={{ height: 34 }} onClick={() => setPendingAction(null)}>Cancel</button>
                                    </div>
                                  </div>
                                ) : null}
                              </>
                            ) : null}

                            {step.progress && step.progress.kind === "generating" && gening ? (
                              <GeneratingPanel code={code} title={step.progress.title} detail={step.progress.detail} />
                            ) : null}

                            {step.key === "generate" && isCurrent && !step.progress ? (
                              <label className="wr-checkrow">
                                <input
                                  type="checkbox"
                                  checked={!proseOff[code]}
                                  onChange={(e) => setProseOff((prev) => ({ ...prev, [code]: !e.target.checked }))}
                                />
                                Include written summary (prose)
                              </label>
                            ) : null}

                            {flowHere ? renderFlow(flowHere) : null}

                            {hasActions ? (
                              <div className="wr-actions">
                                {/* A link with no address does nothing, so it is not drawn. */}
                                {step.secondary ? (
                                  step.key === "publish" ? (
                                    row && row.pdfOpenUrl ? <a className="wr-btn" href={row.pdfOpenUrl} target="_blank" rel="noopener noreferrer" data-testid="report-open-pdf-step">{step.secondary}</a> : null
                                  ) : row && row.wordUrl ? (
                                    <a className="wr-btn" href={row.wordUrl} target="_blank" rel="noopener noreferrer" data-testid={`report-open-word-${code}`}>{step.secondary}</a>
                                  ) : null
                                ) : null}
                                {step.primary ? (
                                  <button type="button" className="wr-btn pri" data-testid={`report-primary-${code}-${step.key}`} disabled={primaryLocked || !action} onClick={action}>
                                    {step.primary}
                                  </button>
                                ) : null}
                                {step.lockNote ? <span className="wr-locknote">{step.lockNote}</span> : null}
                                {generateBlocked ? (
                                  <span className="wr-locknote" data-testid={`report-generation-elsewhere-${code}`}>Another report for this project is generating. Wait for it to finish.</span>
                                ) : null}
                                {step.key === "review" && isCurrent && row ? (
                                  <button type="button" className="wr-linkbtn" data-testid={`report-regenerate-${code}`} disabled={flowBusy} onClick={() => setDialog({ kind: "regenerate", code, reason: "", reportNumber: row.reportNumber, status: row.raw.reportStatus, publishedNumber: row.publishedNumber })}>
                                    Regenerate…
                                  </button>
                                ) : null}
                                {step.key === "publish" && isCurrent && row && !flowBusy ? (
                                  <button type="button" className="wr-linkbtn" data-testid={`report-reopen-${code}`} onClick={() => setDialog({ kind: "reopen", code, reason: "", reportNumber: row.reportNumber, status: row.raw.reportStatus, publishedNumber: row.publishedNumber, etag: row.etag })}>
                                    Reopen as draft…
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <div className="wr-steps" data-testid={`report-covered-${code}`}>
                    {project.mode === "covered" && project.coveredBy ? (
                      <div className="wr-covered">
                        <strong style={{ color: "var(--wr-text)" }}>{project.coveredBy.label}</strong>
                        <span className="mono" style={{ color: "var(--wr-t3)" }}>{project.coveredBy.coversTag}</span>
                      </div>
                    ) : (
                      <div className="wr-hint" data-testid={`report-legacy-${code}`}>No status recorded on this report &mdash; read-only. Open it, and every version, from the list below.</div>
                    )}
                  </div>
                )}

                <aside className="wr-aside" aria-label="How a report is made">
                  <span className="wr-asidelabel">How a report is made</span>
                  <span className="wr-asidebody">{ASIDE_BODY}</span>
                  <span className="wr-asidefoot">Any draft below can be opened, edited and approved, not only the newest.</span>
                </aside>
              </div>

              <div className="wr-livearea" style={{ padding: "0 20px" }} role="status" aria-live="polite">
                {created && created.code === code && !(projectOutcome && projectOutcome.errorCode) ? (
                  <div data-testid={`report-version-created-${code}`} className="wr-created">
                    The new draft is ready — review it in the list below.
                    {created.unapproved ? (
                      <p data-testid={`report-unapproved-${code}`} style={{ margin: "6px 0 0" }}>
                        {unapprovedRecordsText(created.unapproved)}{" "}
                        <a href="#today" data-testid={`report-unapproved-link-${code}`} onClick={(e) => { e.preventDefault(); if (onNavigate) onNavigate("endofday"); }}>Go to End of day</a>
                      </p>
                    ) : null}
                    {created.notes && created.notes.length ? (
                      <ul data-testid={`report-version-notes-${code}`} style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                        {created.notes.map((n) => <li key={n}>{n}</li>)}
                      </ul>
                    ) : null}
                  </div>
                ) : projectOutcome ? (
                  <div data-testid={`report-outcome-${code}`} className={`wr-outcome ${projectOutcome.errorCode ? "bad" : "ok"}`}>
                    {projectOutcome.errorCode
                      ? `${outcomeText(projectOutcome.errorCode, projectOutcome)}${projectOutcome.lines ? " " + projectOutcome.lines.map((l) => `${l.id}: ${l.count}`).join(", ") : ""}${projectOutcome.jobId ? ` (job ${projectOutcome.jobId})` : ""}`
                      : STATUS_TEXT[projectOutcome.status] || "Done."}
                  </div>
                ) : null}
              </div>

              {otherFlow ? <div style={{ padding: "0 20px 12px" }}>{renderFlow(otherFlow)}</div> : null}

              <ReportsTable
                project={project}
                busyNumber={flowBusy ? projectFlow.reportNumber : null}
                openHistory={openHistory}
                onToggleHistory={(key) => setOpenHistory((prev) => ({ ...prev, [key]: !prev[key] }))}
                onApprove={(r) => askApprove(project, r)}
                onPublish={onPublish ? (r) => askPublish(project, r) : undefined}
                onRetryPdf={onRenderPdf ? (r) => retryPdf(project, r) : undefined}
                onSchedule={onSchedule ? (r) => runSchedule(code, r.reportNumber, r.etag, r.displayVersion) : undefined}
                onHold={onHold ? (r) => doHold(project, r) : undefined}
                onLoadVersions={onLoadVersions}
              />
            </section>
          );
        })}
      </main>

      {dialog && dialog.kind === "approve" ? (
        <Dialog labelledBy="wr-dlg-title" onClose={() => setDialog(null)} testId="report-dialog-approve">
          <ApproveBody d={dialog} onCancel={() => setDialog(null)} onConfirm={() => doApprove(dialog)} onChoose={(next) => setDialog({ ...dialog, next })} />
        </Dialog>
      ) : null}

      {dialog && dialog.kind === "publish" ? (
        <Dialog labelledBy="wr-dlg-title" onClose={() => setDialog(null)} testId="report-dialog-publish">
          <PublishBody d={dialog} onCancel={() => setDialog(null)} onConfirm={() => doPublish(dialog)} />
        </Dialog>
      ) : null}

      {dialog && (dialog.kind === "reopen" || dialog.kind === "regenerate") ? (
        <Dialog labelledBy="wr-dlg-title" onClose={() => setDialog(null)} testId={`report-dialog-${dialog.kind}`}>
          <h2 id="wr-dlg-title">{dialog.kind === "reopen" ? `Reopen ${reportRef(dialog.status, dialog.publishedNumber)} as a draft?` : `Regenerate ${reportRef(dialog.status, dialog.publishedNumber)}?`}</h2>
          <p>
            {dialog.kind === "reopen"
              ? "The report goes back to a draft you can edit in Word. The PDF made from it is replaced when you approve again."
              : "Builds a new Word draft from the confirmed data. Your Word edits stay in the version history."}
          </p>
          <textarea
            data-autofocus="true"
            value={dialog.reason}
            onChange={(e) => setDialog({ ...dialog, reason: e.target.value })}
            placeholder="Reason (required)"
            aria-label="Reason"
            data-testid={`report-reason-${dialog.code}`}
          />
          <div className="wr-dlgbtns">
            <button type="button" className="wr-btn" onClick={() => setDialog(null)}>Cancel</button>
            <button
              type="button"
              className="wr-btn pri"
              data-testid={`report-dialog-${dialog.kind}-confirm`}
              disabled={!dialog.reason.trim()}
              onClick={() => {
                const d = dialog;
                if (d.kind === "reopen") doReopen(d, d.reason);
                else {
                  setDialog(null);
                  const project = model.projects.find((p) => p.code === d.code);
                  if (project) generate(project, { reason: d.reason.trim() });
                }
              }}
            >
              {dialog.kind === "reopen" ? "Reopen as draft" : "Regenerate"}
            </button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
